import React, { useState, useEffect, useContext, useRef } from "react";
import { db } from "../firebase";
import { HEADERTEXT_TYPES, WORKFLOW_SCHEDULER_TYPES, WORKFLOW_WERKER_TYPES } from "../functions/postActionTypes";
import { collection, doc, getDocs, updateDoc, where } from "firebase/firestore";
import { fx_FB_JobEditPending } from "../functions/fx_FB_JobEditPending";
import {
  IonContent,
  IonPage,
  IonButton,
  IonLabel,
  IonItem,
  IonInput,
  IonTextarea,
  useIonViewDidEnter,
  IonPicker,
  useIonAlert,
  IonHeader,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonIcon,
  IonSelectOption,
  IonSelect,
  IonList,
  IonItemDivider,
  IonAccordionGroup,
  IonAccordion,
  useIonToast,
} from "@ionic/react";
import "../theme/AppStyles.css";
import { ProfileContext } from "../context/ProfileContext";
import { addDoc, collectionGroup, query, setDoc, writeBatch } from "@firebase/firestore";
import { fx_DocStamp } from "../functions/Fx_DocStamp";
import { PickerColumn } from "@ionic/core";
import { useHistory } from "react-router-dom";
import { fx_JobStamp } from "../functions/Fx_JobStamp";
import { SchedJobContext } from "../context/JobContextScheduler";
import { arrowBack, mapOutline } from "ionicons/icons";
import { getLastSeriesStart, getMaxSeriesIndex } from "../functions/Fx_ScheduleJobSeriesQueries";
// Recurrence (v1)
import type { RepeatPattern, SeriesChoice } from "../functions/Fx_ScheduleJobTypes";
import { addDaysLocal, addHoursIso, isPastByMs } from "../functions/Fx_ScheduleJobDates";
import { createSeriesJobDocsBatch } from "../functions/Fx_ScheduleJobSeriesWrites";
import { useJobFormValidation } from "../hooks/scheduler/useJobFormValidation";

interface ScheduleJobProps {
  desktopMode?: {
    isDesktopMode: boolean;
    mode: "create" | "edit" | "clone";
    jobDocId?: string;
    onComplete: (result?: {
      nextView?: "details" | "selectWerkers" | "selectWerkersSeries";
      jobId?: string;
      seriesId?: string;
      jobIds?: string[];
    }) => void;
  };
}

const ScheduleJob: React.FC<ScheduleJobProps> = ({ desktopMode }) => {
  const { schedJob } = useContext(SchedJobContext);
  // Keep a stable ref to schedJob so loadJobData doesn't re-run on every Firestore update
  const schedJobRef = React.useRef(schedJob);
  React.useEffect(() => { schedJobRef.current = schedJob; }, [schedJob]);
  const { profile, usersID } = useContext(ProfileContext);
  const [jobId, setJobId] = React.useState("");
  const [beginSelectedDate, setBeginSelectedDate] = useState("2025-03-13T03:00");
  const [endSelectedDate, setEndSelectedDate] = useState(new Date().toISOString());
  const [company, setCompany] = React.useState("");
  const [location, setLocation] = React.useState("");
  const [locationCoordinates, setLocationCoordinates] = React.useState("");
  const [pay, setPay] = React.useState("");
  const [numberOfWorkers, setnumberOfWorkers] = React.useState<number>(1);
  const [notes, setNotes] = React.useState("");
  
  // Validation colors and logic via hook
  const {
    jobIdColor,
    companyColor,
    locationColor,
    numWerkersColor,
    beginDateColor,
    endDateColor,
    repeatOccColor,
    setRepeatColor,
    setRepeatOccColor,
    validate,
  } = useJobFormValidation();
  
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const [headerText, setHeaderText] = useState(HEADERTEXT_TYPES.ScheduleJob);
  // Recurrence (v1)

  const [repeatPattern, setRepeatPattern] = useState<RepeatPattern>("none");
  const [repeatOccurrences, setRepeatOccurrences] = useState<number>(2);
  const [existingSeriesId, setExistingSeriesId] = useState<string | null>(null);

  const [showMapPin, setShowMapPin] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();

  const bottomRef = useRef<null | HTMLDivElement>(null);
  const accGroupElRef = React.useRef<HTMLIonAccordionGroupElement | null>(null);
  const history = useHistory<any>();
  
  // Support both desktop mode (props) and mobile mode (history state)
  const navState = history.location.state as { 
    msgCat?: "JobClone" | "JobEdit"; 
    jobDocId?: string;
  } | undefined;

  const isDesktopMode = desktopMode?.isDesktopMode ?? false;
  const isJobEdit = desktopMode ? desktopMode.mode === "edit" : (navState?.msgCat === "JobEdit");
  const isJobClone = desktopMode ? desktopMode.mode === "clone" : (navState?.msgCat === "JobClone");
  const jobDocIdFromState = desktopMode?.jobDocId ?? navState?.jobDocId;

  // Universal navigation function - uses desktop callback if available, otherwise history.goBack()
  const navigateBack = () => {
    if (desktopMode?.onComplete) {
      desktopMode.onComplete();
    } else {
      history.goBack();
    }
  };

  const hasMapValue = !!((location || "").trim() || (locationCoordinates || "").trim());

  const setAccGroup = React.useCallback((el: HTMLIonAccordionGroupElement | null) => {
    accGroupElRef.current = el;
    // Set initial open state for desktop mode
    if (el && isDesktopMode) {
      requestAnimationFrame(() => {
        (el as any).value = ["repeat", "details", "payStaffing", "notes"];
      });
    }
  }, [isDesktopMode]);

  // Create job doc function ///////////
  const createJobDoc = async (docTimeStamp: number) => {
    // 1 - create the docStamp for the jobDoc
    const docStamp = fx_DocStamp(
      docTimeStamp, // timestamp
      profile.UserId, // to Uid
      profile.FirstName, // to fName
      profile.LastName, // to lName
      profile.ProfilePicURL, // to Pic URL
      profile.UserId, // from Uid
      profile.FirstName, // from fName
      profile.LastName, // from L Name
      profile.ProfilePicURL, // from Pic URL
      "NA", // docInfo_docId
      "NA", // docInfo_docPath
      "NA", // docInfo_docPathCollection
      "NA", // docInfo_parentDocId
      "NA", // docInfo_parentDocPath
      "NA" // docInfo_parentDocPathCollection
    );

    // 2 - create the jobStamp for the jobDoc
    const jobInfo = fx_JobStamp(
      "",
      company,
      beginSelectedDate,
      endSelectedDate,
      location,
      locationCoordinates,
      pay,
      jobId, // scheduler chosen
      notes,
      numberOfWorkers,
      false,
      false,
      false,
      false,
      true,
      false,
      WORKFLOW_SCHEDULER_TYPES.Open,
      "",
      0
    );

    // 3 - create the jobDoc in firestore
    const jobRef = collection(db, "jobsDocs");
    const docRef = await addDoc(jobRef, {
      docStamp,
      jobInfo,
    });
    return docRef;
  };
  // End of create job doc function ///////////

  // Form validation - using hook //////////////
  const formValidation = async () => {
    return validate({
      jobId,
      beginSelectedDate,
      endSelectedDate,
      company,
      location,
      numberOfWorkers,
      repeatPattern,
      repeatOccurrences,
    });
  };

  // Confirm edit scope (single vs. series) /////////////
  type EditScope = "single" | "series" | "cancel";

  const confirmEditScopeIfNeeded = async (): Promise<EditScope> => {
    // Only relevant when editing an existing job that is already part of a series
    const msgCat = desktopMode
      ? (desktopMode.mode === 'edit' ? 'JobEdit' : desktopMode.mode === 'clone' ? 'JobClone' : undefined)
      : history?.location?.state?.msgCat as "JobEdit" | "JobClone" | undefined;

    if (msgCat !== "JobEdit") return "single";
    if (!existingSeriesId) return "single";

    return new Promise((resolve) => {
      presentAlert({
        header: "Edit repeating job",
        message:
          "Do you want to apply your changes to only this job, or the whole series? Whole series edit will not update dates/times, only job details.",

        backdropDismiss: false,
        buttons: [
          { text: "Cancel", role: "cancel", handler: () => resolve("cancel") },
          { text: "This Job Only", handler: () => resolve("single") },
          { text: "Whole Series", handler: () => resolve("series") },
        ],
      });
    });
  };

  // Confirm with user if saving a date in the past
  const confirmPastDateIfNeeded = async (): Promise<boolean> => {
    const startPast = isPastByMs(beginSelectedDate);
    const finishPast = isPastByMs(endSelectedDate);
    const isPast = startPast || finishPast;

    // Not past → proceed normally
    if (!isPast) return true;

    // Past + repeat enabled → block series entirely
    if (repeatPattern !== "none") {
      return new Promise((resolve) => {
        presentAlert({
          header: "Date is in the past",
          message: "You can’t save a repeating series that starts in the past. Please change the date and try again.",
          backdropDismiss: false,
          buttons: [
            {
              text: "OK",
              handler: () => resolve(false),
            },
          ],
        });
      });
    }

    // Past + single job → allow Save Anyway
    return new Promise((resolve) => {
      presentAlert({
        header: "Date is in the past",
        message: "This job’s start or finish time is in the past. Do you want to save it anyway?",
        backdropDismiss: false,
        buttons: [
          { text: "Go Back", role: "cancel", handler: () => resolve(false) },
          { text: "Save Anyway", handler: () => resolve(true) },
        ],
      });
    });
  };

  const confirmSeriesChoiceIfNeeded = async (): Promise<SeriesChoice> => {
    // If user didn't enable repeat, it's a single job.
    if (repeatPattern === "none") return "single";

    // Repeat enabled, but job is not already in series
    if (!existingSeriesId) {
      return new Promise((resolve) => {
        // console.log("presenting series choice alert NOT in a Series");
        presentAlert({
          header: "Create repeating series?",
          message:
            repeatPattern === "daily"
              ? `Create ${repeatOccurrences} daily jobs starting at this job’s start time?`
              : `Create ${repeatOccurrences} weekly jobs starting at this job’s start time?`,
          buttons: [
            {
              text: "Cancel",
              role: "cancel",
              handler: () => resolve("single"),
            },
            {
              text: "Create Series",
              handler: () => resolve("create_new_series"),
            },
          ],
        });
      });
    }

    // Repeat enabled AND base job is already in a series
    return new Promise((resolve) => {
      // console.log("presenting series choice alert IN a Series");
      presentAlert({
        header: "Already part of a series",
        message:
          "This job belongs to an existing series. Do you want to continue the existing series or start a new series from this job?",
        buttons: [
          { text: "Cancel", role: "cancel", handler: () => resolve("single") },
          {
            text: "Start New Series",
            handler: () => resolve("create_new_series"),
          },
          {
            text: "Continue Existing",
            handler: () => resolve("continue_existing_series"),
          },
        ],
      });
    });
  };
  // End of confirmSeriesChoiceIfNeeded ///////////

  // Handle form submission /////////////
  const handleSubmit = async () => {
    if (isSaving) return;

    try {
      setIsSaving(true);

      // ✅ 1) Validate first (validation resets colors automatically)
      const result = await formValidation();
      if (!result) return;

      // Identify edit mode early
      const msgCat = desktopMode
        ? (desktopMode.mode === 'edit' ? 'JobEdit' : desktopMode.mode === 'clone' ? 'JobClone' : undefined)
        : history?.location?.state?.msgCat as "JobEdit" | "JobClone" | undefined;

      // If editing a job that is already in a series, ask scope FIRST.
      // Both "Whole Series" and "This Job Only" route through the same cloud function
      // (jobEditPending) — series edits skip dates, single edits include them.
      if (msgCat === "JobEdit" && existingSeriesId) {
        const scope = await confirmEditScopeIfNeeded();

        if (scope === "cancel") return;

        if (scope === "series") {
          // Trigger async series edit processing
          try {
            await fx_FB_JobEditPending(
              usersID,
              (jobDocIdFromState as string | undefined) || "",
              existingSeriesId,
              "series",
              {
                shiftIdentifier: jobId,
                company,
                location,
                locationCoordinates,
                pay,
                shiftNotes: notes,
                numJobSlots: numberOfWorkers,
                // Series edits don't change dates
                dateTimeStart: null,
                dateTimeFinish: null,
              },
              {
                firstName: profile.FirstName,
                userId: usersID,
              }
            );

            // Show immediate feedback toast
            await presentToast({
              message: "Updating series...",
              duration: 2000,
              position: "top",
              color: "medium",
            });
          } catch (error) {
            console.error("Error triggering series edit:", error);
            await presentToast({
              message: "Failed to trigger update. Please try again.",
              duration: 2500,
              position: "bottom",
              color: "danger",
            });
            return;
          }

          navigateBack();
          return;
        }

        if (scope === "single") {
          // Trigger async single-occurrence edit via the same cloud function as series.
          // Returns immediately so we don't chain another alert (which previously caused a hang).
          const singleJobDocId = (jobDocIdFromState as string | undefined) || "";
          if (!singleJobDocId) {
            await presentToast({
              message: "Missing job reference. Please try again.",
              duration: 2500,
              position: "bottom",
              color: "danger",
            });
            return;
          }

          try {
            await fx_FB_JobEditPending(
              usersID,
              singleJobDocId,
              null, // No series ID for single edit
              "single",
              {
                shiftIdentifier: jobId,
                company,
                location,
                locationCoordinates,
                pay,
                shiftNotes: notes,
                numJobSlots: numberOfWorkers,
                dateTimeStart: beginSelectedDate,
                dateTimeFinish: endSelectedDate,
              },
              {
                firstName: profile.FirstName,
                userId: usersID,
              }
            );

            await presentToast({
              message: "Updating job...",
              duration: 2000,
              position: "top",
              color: "medium",
            });
          } catch (error) {
            console.error("Error triggering single job edit:", error);
            await presentToast({
              message: "Failed to trigger update. Please try again.",
              duration: 2500,
              position: "bottom",
              color: "danger",
            });
            return;
          }

          navigateBack();
          return;
        }
      }

      // ✅ 2) Then confirm past-date (only applies when we might change dates)
      const okToProceed = await confirmPastDateIfNeeded();
      if (!okToProceed) return;

      // ✅ 3) Then confirm series choice (only prompts if repeat enabled)
      const seriesChoice: SeriesChoice = msgCat === "JobEdit" ? "single" : await confirmSeriesChoiceIfNeeded();

      const buildSeriesWriteArgs = (overrides: {
        seriesId: string;
        seriesStartIsoLocal: string;
        occurrences: number;
        seriesIndexStart: number;
        seriesTotal: number;
      }) => {
        const pattern: RepeatPattern =
          repeatPattern === "weekly" ? "weekly" : repeatPattern === "daily" ? "daily" : "none";

        return {
          profile,
          company,
          location,
          locationCoordinates,
          pay,
          jobId,
          notes,
          numberOfWorkers,
          repeatPattern: pattern === "none" ? "daily" : pattern, // (won’t be used when seriesChoice !== "single")
          endSelectedDate,
          jobStatusSched: WORKFLOW_SCHEDULER_TYPES.Open,
          ...overrides,
        };
      };

      const currentJobDocId = jobDocIdFromState as string | undefined;

      // Shared edit updater (used by single edit + edit+continue-series)
      const runEditUpdate = async () => {
        if (!currentJobDocId) return;
        const updateJobRef = query(
          collectionGroup(db, "jobsDocs"),
          where("jobInfo.core.uniqueJobId", "==", currentJobDocId)
        );
        const querySnapshot = await getDocs(updateJobRef);

        const ops: Promise<any>[] = [];
        querySnapshot.forEach((docSnap) => {
          const docInfo = docSnap.data();

          const toUid = docInfo?.docStamp?.userInfoTo?.uid as string | undefined;
          const werkerStatus = docInfo?.jobInfo?.status?.jobStatusWerker as string | undefined;
          const isSchedulerCopy = toUid === usersID;

          let switchIsRead: boolean = false;
          if (docInfo?.docStamp?.userInfoTo?.uid === usersID) {
            switchIsRead = true;
          }
          ops.push(
            updateDoc(docSnap.ref, {
              "jobInfo.core.shiftIdentifier": jobId,
              "jobInfo.core.dateTimeStart": beginSelectedDate,
              "jobInfo.core.dateTimeFinish": endSelectedDate,
              "jobInfo.core.company": company,
              "jobInfo.core.location": location,
              "jobInfo.core.locationCoordinates": locationCoordinates,
              "jobInfo.core.pay": pay,
              "jobInfo.core.shiftNotes": notes,
              "jobInfo.core.numJobSlots": numberOfWorkers,
              "jobInfo.status.isRead": switchIsRead,
            })
          );
          // Update per-user Available feed snapshot (only for invited werkers)
          if (toUid && !isSchedulerCopy && werkerStatus === WORKFLOW_WERKER_TYPES.Invited) {
            const feedRef = doc(db, "users", toUid, "jobFeed", currentJobDocId);
            ops.push(
              setDoc(
                feedRef,
                {
                  company,
                  location,
                  locationCoordinates,
                  pay,
                  shiftIdentifier: jobId,
                  shiftNotes: notes,
                  startDateTimeRaw: beginSelectedDate,
                  finishDateTimeRaw: endSelectedDate,
                  updatedAt: Date.now(),
                  // IMPORTANT: do NOT set isRead / isNewMessage here (preserve user flags)
                },
                { merge: true }
              )
            );
          }
        });
        await Promise.all(ops);
      };

      // If user chose a series action, handle it here
      if (seriesChoice !== "single") {
        // Determine seriesId (new or existing)
        // console.log("seriesChoice =", seriesChoice);
        const seriesId =
          seriesChoice === "continue_existing_series" && existingSeriesId
            ? existingSeriesId
            : doc(collection(db, "jobsDocs")).id;

        // ----------------------------
        // JobEdit flow
        // ----------------------------
        if (msgCat === "JobEdit") {
          // A) JobEdit + Create New Series
          if (seriesChoice === "create_new_series") {
            if (!currentJobDocId) return;

            const updateJobRef = query(
              collectionGroup(db, "jobsDocs"),
              where("jobInfo.core.uniqueJobId", "==", currentJobDocId)
            );
            const snap = await getDocs(updateJobRef);

            const seriesTotal = repeatOccurrences;

            // Update edited job to become occurrence #1 of new series
            const ops: Promise<any>[] = [];
            snap.forEach((docSnap) => {
              ops.push(
                updateDoc(docSnap.ref, {
                  "jobInfo.core.shiftIdentifier": jobId,
                  "jobInfo.core.dateTimeStart": beginSelectedDate,
                  "jobInfo.core.dateTimeFinish": endSelectedDate,
                  "jobInfo.core.company": company,
                  "jobInfo.core.location": location,
                  "jobInfo.core.locationCoordinates": locationCoordinates,
                  "jobInfo.core.pay": pay,
                  "jobInfo.core.shiftNotes": notes,
                  "jobInfo.core.numJobSlots": numberOfWorkers,
                  "jobInfo.core.seriesId": seriesId,
                  "jobInfo.core.seriesIndex": 1,
                  "jobInfo.core.seriesTotal": seriesTotal,
                })
              );
            });
            await Promise.all(ops);

            // Create remaining occurrences (#2..#N)
            const remaining = repeatOccurrences - 1;

            const extraJobIds =
              remaining > 0
                ? await createSeriesJobDocsBatch(
                    buildSeriesWriteArgs({
                      seriesId,
                      seriesStartIsoLocal: beginSelectedDate,
                      occurrences: remaining,
                      seriesIndexStart: 2,
                      seriesTotal,
                    })
                  )
                : [];

            const jobIds = [currentJobDocId, ...extraJobIds];

            history.replace({
              pathname: `/SelectWerkersSeries/${seriesId}`,
              state: { seriesId, jobIds, from: "ScheduleJob", cameFromDetail: !!msgCat },
            });
            return;
          }

          // B) JobEdit + Continue Existing Series (v1.5 indexing)
          if (seriesChoice === "continue_existing_series" && existingSeriesId) {
            // 1) Perform the edit update (no series metadata changes here)
            await runEditUpdate();

            // 2) Append NEW occurrences after last existing occurrence
            const lastStart = await getLastSeriesStart(profile.UserId, existingSeriesId);

            const startFrom = lastStart
              ? addDaysLocal(lastStart, repeatPattern === "weekly" ? 7 : 1)
              : beginSelectedDate;

            const maxIndex = await getMaxSeriesIndex(profile.UserId, existingSeriesId);
            const seriesIndexStart = maxIndex + 1;
            const seriesTotal = maxIndex + repeatOccurrences;

            const appendedJobIds = await createSeriesJobDocsBatch(
              buildSeriesWriteArgs({
                seriesId: existingSeriesId,
                seriesStartIsoLocal: startFrom,
                occurrences: repeatOccurrences,
                seriesIndexStart,
                seriesTotal,
              })
            );

            history.replace({
              pathname: `/SelectWerkersSeries/${existingSeriesId}`,
              state: {
                seriesId: existingSeriesId,
                jobIds: appendedJobIds,
                from: "ScheduleJob",
                cameFromDetail: !!msgCat,
              },
            });
            return;
          }
        }

        // ----------------------------
        // Schedule / Clone flow
        // ----------------------------
        if (seriesChoice === "continue_existing_series" && existingSeriesId) {
          const lastStart = await getLastSeriesStart(profile.UserId, existingSeriesId);

          const startFrom = lastStart ? addDaysLocal(lastStart, repeatPattern === "weekly" ? 7 : 1) : beginSelectedDate;

          const maxIndex = await getMaxSeriesIndex(profile.UserId, existingSeriesId);
          const seriesIndexStart = maxIndex + 1;
          const seriesTotal = maxIndex + repeatOccurrences;

          const appendedJobIds = await createSeriesJobDocsBatch(
            buildSeriesWriteArgs({
              seriesId: existingSeriesId,
              seriesStartIsoLocal: startFrom,
              occurrences: repeatOccurrences,
              seriesIndexStart,
              seriesTotal,
            })
          );

          if (desktopMode?.onComplete) {
            desktopMode.onComplete({
              nextView: "selectWerkersSeries",
              seriesId: existingSeriesId,
              jobIds: appendedJobIds,
              jobId: appendedJobIds[0],
            });
            return;
          }

          if (profile.SubscriptionType !== "Scheduler") {
            history.replace({
              pathname: "/Paywall",
              state: {
                cameFrom: "ScheduleJob",
                postCreate: {
                  jobId: appendedJobIds[0],
                  seriesId: existingSeriesId,
                  jobIds: appendedJobIds,
                  hasAssociates: true,
                  cameFromDetail: !!msgCat,
                },
              },
            });
            return;
          }

          history.replace({
            pathname: `/SelectWerkersSeries/${existingSeriesId}`,
            state: {
              seriesId: existingSeriesId,
              jobIds: appendedJobIds,
              from: "ScheduleJob",
              cameFromDetail: !!msgCat,
            },
          });
          return;
        } else {
          // create_new_series (Schedule / Clone)
          const jobIds = await createSeriesJobDocsBatch(
            buildSeriesWriteArgs({
              seriesId,
              seriesStartIsoLocal: beginSelectedDate,
              occurrences: repeatOccurrences,
              seriesIndexStart: 1,
              seriesTotal: repeatOccurrences,
            })
          );

          if (desktopMode?.onComplete) {
            desktopMode.onComplete({
              nextView: "selectWerkersSeries",
              seriesId,
              jobIds,
              jobId: jobIds[0],
            });
            return;
          }

          if (profile.SubscriptionType !== "Scheduler") {
            history.replace({
              pathname: "/Paywall",
              state: {
                cameFrom: "ScheduleJob",
                postCreate: {
                  jobId: jobIds[0],
                  seriesId,
                  jobIds,
                  hasAssociates: true,
                  cameFromDetail: !!msgCat,
                },
              },
            });
            return;
          }

          history.replace({
            pathname: `/SelectWerkersSeries/${seriesId}`,
            state: { seriesId, jobIds, from: "ScheduleJob", cameFromDetail: !!msgCat },
          });
          return;
        }
      }

      // ✅ Single job path
      let docDocId: string = "";

      // Single edit path (ask scope if part of series)
      if (msgCat === "JobEdit") {
        // Trigger async single job edit processing
        if (currentJobDocId) {
          try {
            await fx_FB_JobEditPending(
              usersID,
              currentJobDocId,
              null, // No series ID for single edit
              "single",
              {
                shiftIdentifier: jobId,
                company,
                location,
                locationCoordinates,
                pay,
                shiftNotes: notes,
                numJobSlots: numberOfWorkers,
                dateTimeStart: beginSelectedDate,
                dateTimeFinish: endSelectedDate,
              },
              {
                firstName: profile.FirstName,
                userId: usersID,
              }
            );

            // Show immediate feedback toast
            await presentToast({
              message: "Updating job...",
              duration: 2000,
              position: "top",
              color: "medium",
            });
          } catch (error) {
            console.error("Error triggering job edit:", error);
            await presentToast({
              message: "Failed to trigger update. Please try again.",
              duration: 2500,
              position: "bottom",
              color: "danger",
            });
            return;
          }
        }

        navigateBack();
        return;
      }

      // Single create path
      const docTime = Date.now();
      const docRef = await createJobDoc(docTime);
      docDocId = docRef.id;

      const setJobIdRef = doc(db, "jobsDocs", docDocId);
      await updateDoc(setJobIdRef, {
        "jobInfo.core.uniqueJobId": docDocId,
      });

      if (profile.Associates.length === 1 && profile.Associates[0] === "XXX") {
        if (!desktopMode?.onComplete && profile.SubscriptionType !== "Scheduler") {
          history.replace({
            pathname: "/Paywall",
            state: {
              cameFrom: "ScheduleJob",
              postCreate: { jobId: docDocId, hasAssociates: false },
            },
          });
          return;
        }
        window.location.href = "/SchedShiftDetails/" + docDocId;
      } else {
        if (desktopMode?.onComplete) {
          desktopMode.onComplete({ nextView: "selectWerkers", jobId: docDocId });
          return;
        }

        if (profile.SubscriptionType !== "Scheduler") {
          history.replace({
            pathname: "/Paywall",
            state: {
              cameFrom: "ScheduleJob",
              postCreate: { jobId: docDocId, hasAssociates: true },
            },
          });
          return;
        }

        history.replace({
          pathname: `/SelectWerkers/${docDocId}`,
          state: { from: "ScheduleJob" },
        });
      }
      presentToast({
        message: "Saved",
        duration: 1200,
        position: "bottom",
      });
    } catch (err: any) {
      // console.log("ScheduleJob save failed:", err);
      presentToast({
        message: err?.message ? `Save failed: ${err.message}` : "Save failed. Please try again.",
        duration: 3000,
        position: "bottom",
      });
    } finally {
      setIsSaving(false);
    }
  };
  // End of handleSubmit ///////////

  const NumberColumn = {
    name: "number",
    options: [
      { text: "1", value: 1 },
      { text: "2", value: 2 },
      { text: "3", value: 3 },
      { text: "4", value: 4 },
      { text: "5", value: 5 },
      { text: "6", value: 6 },
      { text: "7", value: 7 },
      { text: "8", value: 8 },
      { text: "9", value: 9 },
      { text: "10", value: 10 },
      { text: "11", value: 11 },
      { text: "12", value: 12 },
      { text: "13", value: 13 },
      { text: "14", value: 14 },
      { text: "15", value: 15 },
      { text: "16", value: 16 },
      { text: "17", value: 17 },
      { text: "18", value: 18 },
      { text: "19", value: 19 },
      { text: "20", value: 20 },
      { text: "21", value: 21 },
      { text: "22", value: 22 },
      { text: "23", value: 23 },
      { text: "24", value: 24 },
      { text: "25", value: 25 },
    ],
  } as PickerColumn;

  // Load job data - works for both mobile (useIonViewDidEnter) and desktop (useEffect)
  const loadJobData = React.useCallback(async () => {
    if (!jobDocIdFromState) return;

    if (isJobEdit) {
      setHeaderText(HEADERTEXT_TYPES.EditJob);
    } else if (isJobClone) {
      setHeaderText(HEADERTEXT_TYPES.CloneJob);
    }

    const thisSchedJob = await schedJobRef.current.find((schedJob: any) => schedJob.JobDocId === jobDocIdFromState);

    if (!thisSchedJob) return;

    const foundSeriesId = (thisSchedJob as any)?.seriesId ?? (thisSchedJob as any)?.jobInfo?.core?.seriesId ?? null;

    setExistingSeriesId(
      typeof foundSeriesId === "string" && foundSeriesId.trim().length > 0 ? foundSeriesId : null
    );

    const timezoneOffset: number = new Date().getTimezoneOffset() / 60;
    const adjStartTime = addHoursIso(thisSchedJob.StartDateTimeRaw, timezoneOffset);
    const adjEndTime = addHoursIso(thisSchedJob.FinishDateTimeRaw, timezoneOffset);

    setJobId(thisSchedJob.ShiftIdentifier);
    setBeginSelectedDate(new Date(adjStartTime).toISOString().substring(0, 19));
    setEndSelectedDate(new Date(adjEndTime).toISOString().substring(0, 19));
    setCompany(thisSchedJob.Company);
    setLocation(thisSchedJob.Location);
    setLocationCoordinates(thisSchedJob.LocationCoordinates);
    setPay(thisSchedJob.Pay);
    setnumberOfWorkers(thisSchedJob.NumJobSlots);
    setNotes(thisSchedJob.ShiftNotes);
    setRepeatPattern("none");
    setRepeatOccurrences(2);
    setRepeatColor("dark");
    setRepeatOccColor("dark");
  }, [jobDocIdFromState, isJobEdit, isJobClone, setRepeatColor, setRepeatOccColor]);

  const resetForNewJob = React.useCallback(() => {
    const today = new Date().toISOString();
    const timezoneOffset: number = new Date().getTimezoneOffset() / 60;
    const adjStartTime = addHoursIso(today, timezoneOffset);
    const adjEndTime = addHoursIso(today, timezoneOffset);

    setHeaderText(HEADERTEXT_TYPES.ScheduleJob);
    setJobId("");
    setCompany("");
    setLocation("");
    setLocationCoordinates("");
    setPay("");
    setnumberOfWorkers(1);
    setNotes("");
    setBeginSelectedDate(new Date(adjStartTime).toISOString().substring(0, 19));
    setEndSelectedDate(new Date(adjEndTime).toISOString().substring(0, 19));
    setRepeatPattern("none");
    setRepeatOccurrences(2);
    setRepeatColor("dark");
    setRepeatOccColor("dark");
    setExistingSeriesId(null);
    setShowMapPin(false);
  }, [setRepeatColor, setRepeatOccColor]);

  // Desktop mode: use useEffect
  useEffect(() => {
    if (!isDesktopMode) return;

    if (jobDocIdFromState) {
      loadJobData();
    } else {
      resetForNewJob();
    }
  }, [isDesktopMode, jobDocIdFromState, loadJobData, resetForNewJob]);

  // Mobile mode: use useIonViewDidEnter
  useIonViewDidEnter(() => {
    if (!isDesktopMode) {
      const navState = history.location.state as { msgCat?: "JobClone" | "JobEdit"; jobDocId?: string } | undefined;

      (accGroupElRef.current as any).value = ["details", "payStaffing", "notes"];

      if (navState?.msgCat === "JobClone" || navState?.msgCat === "JobEdit") {
        if (navState?.msgCat === "JobClone") {
          setHeaderText(HEADERTEXT_TYPES.CloneJob);
        } else if (navState?.msgCat === "JobEdit") {
          setHeaderText(HEADERTEXT_TYPES.EditJob);
        }

        const filterMyJob = async () => {
          const thisSchedJob = await schedJob.find((schedJob: any) => schedJob.JobDocId === navState?.jobDocId);

          if (!thisSchedJob) return;

          const foundSeriesId = (thisSchedJob as any)?.seriesId ?? (thisSchedJob as any)?.jobInfo?.core?.seriesId ?? null;

          setExistingSeriesId(
            typeof foundSeriesId === "string" && foundSeriesId.trim().length > 0 ? foundSeriesId : null
          );

          const timezoneOffset: number = new Date().getTimezoneOffset() / 60;
          const adjStartTime = addHoursIso(thisSchedJob.StartDateTimeRaw, timezoneOffset);
          const adjEndTime = addHoursIso(thisSchedJob.FinishDateTimeRaw, timezoneOffset);

          setJobId(thisSchedJob.ShiftIdentifier);
          setBeginSelectedDate(new Date(adjStartTime).toISOString().substring(0, 19));
          setEndSelectedDate(new Date(adjEndTime).toISOString().substring(0, 19));
          setCompany(thisSchedJob.Company);
          setLocation(thisSchedJob.Location);
          setLocationCoordinates(thisSchedJob.LocationCoordinates);
          setPay(thisSchedJob.Pay);
          setnumberOfWorkers(thisSchedJob.NumJobSlots);
          setNotes(thisSchedJob.ShiftNotes);
          setRepeatPattern("none");
          setRepeatOccurrences(2);
          setRepeatColor("dark");
          setRepeatOccColor("dark");
        };
        filterMyJob();
      } else {
        const today = new Date().toISOString();

        const timezoneOffset: number = new Date().getTimezoneOffset() / 60;
        const adjStartTime = addHoursIso(today, timezoneOffset);
        const adjEndTime = addHoursIso(today, timezoneOffset);

        setBeginSelectedDate(new Date(adjStartTime).toISOString().substring(0, 19));
        setEndSelectedDate(new Date(adjEndTime).toISOString().substring(0, 19));

        setRepeatPattern("none");
        setRepeatOccurrences(2);
        setRepeatColor("dark");
        setRepeatOccColor("dark");

        setExistingSeriesId(null);
      }
    }
  });

  useEffect(() => {
    // console.log(beginSelectedDate);
    // console.log(endSelectedDate);
  }, [beginSelectedDate, endSelectedDate]);

  // Render header for desktop mode
  const renderDesktopHeader = () => (
    <div
      style={{
        backgroundColor: "var(--ion-background-color)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: 56,
        padding: "0 12px",
        boxSizing: "border-box",
        borderBottom: "1px solid var(--ion-color-light-shade)",
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
    >
      <IonButton onClick={navigateBack} fill="clear" style={{ margin: 0 }}>
        <IonIcon
          icon={arrowBack}
          size="large"
          color="dark"
        />
      </IonButton>
      
      <div style={{ flex: 1, textAlign: "center" }}>
        <strong><h2 style={{ margin: 0, lineHeight: "24px" }}>{headerText}</h2></strong>
      </div>
      
      <IonButton
        onClick={handleSubmit}
        fill="clear"
        color="dark"
        disabled={isSaving}
        style={{ margin: 0 }}
      >
        <strong>{isSaving ? "Saving..." : "Save"}</strong>
      </IonButton>
    </div>
  );

  // Desktop mode: render without IonPage wrapper
  if (isDesktopMode) {
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {renderDesktopHeader()}
        <div style={{ flex: 1, overflowY: "auto", padding: "0", paddingBottom: "90px" }}>
          <div>
        {/* CORE (always visible) */}
        <IonList
          inset={true}
        >
          <IonItemDivider>
            <IonLabel
              color={jobIdColor}
            >
              <strong>Job Name/ID:</strong>
            </IonLabel>
          </IonItemDivider>

          <IonItem>
            {/* <IonLabel
              position="stacked"
              color={jobIdColor}
            >
              <h2>Job Name:</h2>
            </IonLabel> */}
            <IonInput
              placeholder="Job name, number or ID"
              type="text"
              name="JobId"
              spellCheck="true"
              autoCapitalize="sentences"
              onIonChange={(e: any) => setJobId(e.detail.value)}
              value={jobId}
              clearInput
            ></IonInput>
          </IonItem>

          <IonItemDivider>
            <IonLabel
              color={beginDateColor}
            >
              <strong>Start:</strong>
            </IonLabel>
          </IonItemDivider>
          <IonItem>
            <IonInput
              type="datetime-local"
              value={beginSelectedDate.substring(0, 19)}
              onIonChange={(e: any) => setBeginSelectedDate(e.detail.value)}
            ></IonInput>
          </IonItem>

          <IonItemDivider>
            <IonLabel
              color={endDateColor}
            >
              <strong>End:</strong>
            </IonLabel>
          </IonItemDivider>
          <IonItem>
            <IonInput
              type="datetime-local"
              value={endSelectedDate.substring(0, 19)}
              onIonChange={(e: any) => setEndSelectedDate(e.detail.value)}
            ></IonInput>
          </IonItem>
        </IonList>

        {/* SECTIONS (accordion, open by default) */}
        {/* SECTIONS (Accordion: open by default, uncontrolled) */}
        <IonAccordionGroup
          ref={setAccGroup as any}
          multiple={true}
        >
          {/* Repeat (disabled in Edit Job to avoid series create/continue conflicts) */}
          {!isJobEdit ? (
            <IonAccordion
              value="repeat"
            >
              <IonItem
                slot="header"
                color="light"
              >
                <IonLabel>
                  <strong>Repeat</strong>
                </IonLabel>
              </IonItem>

              <div slot="content">
                <IonList
                  inset={true}
                >
                  <IonItem>
                    {/* <IonLabel
                    position="stacked"
                    color={repeatColor}
                  >
                    <h2>Repeat:</h2>
                  </IonLabel> */}

                    <IonSelect
                      value={repeatPattern}
                      interface="popover"
                      onIonChange={(e: any) => {
                        const val = (e?.detail?.value as any) || "none";
                        setRepeatPattern(val);
                        setRepeatColor("dark");

                        if (val !== "none" && (!repeatOccurrences || repeatOccurrences < 2)) {
                          setRepeatOccurrences(2);
                        }
                      }}
                    >
                      <IonSelectOption
                        value="none"
                      >
                        None
                      </IonSelectOption>
                      <IonSelectOption
                        value="daily"
                      >
                        Daily
                      </IonSelectOption>
                      <IonSelectOption
                        value="weekly"
                      >
                        Weekly
                      </IonSelectOption>
                    </IonSelect>
                  </IonItem>

                  {repeatPattern !== "none" ? (
                    <IonItem
                    >
                      <IonLabel
                        position="stacked"
                        color={repeatOccColor}
                      >
                        <h2>Number of times recurring:</h2>
                      </IonLabel>

                      <IonInput
                        type="number"
                        inputmode="numeric"
                        value={repeatOccurrences}
                        min={2}
                        onIonChange={(e: any) => {
                          const raw = e?.target?.value ?? e?.detail?.value;
                          const n = Number(raw);
                          setRepeatOccurrences(Number.isFinite(n) ? n : 2);
                          setRepeatOccColor("dark");
                        }}
                      />
                    </IonItem>
                  ) : null}
                </IonList>
              </div>
            </IonAccordion>
          ) : null}

          {/* Details */}
          <IonAccordion
            value="details"
          >
            <IonItem
              slot="header"
              color="light"
            >
              <IonLabel>
                <strong>Details</strong>
              </IonLabel>
            </IonItem>

            <div slot="content">
              <IonList
                inset={true}
              >
                <IonItemDivider
                >
                  <IonLabel
                    color={companyColor}
                  >
                    <strong>Company:</strong>
                  </IonLabel>
                </IonItemDivider>
                <IonItem>
                  <IonInput
                    type="text"
                    name="Company"
                    spellCheck="true"
                    autoCapitalize="sentences"
                    onIonChange={(e: any) => setCompany(e.detail.value)}
                    value={company}
                    clearInput
                  ></IonInput>
                </IonItem>

                <IonItemDivider
                >
                  <IonLabel
                    color={locationColor}
                  >
                    <strong>Location:</strong>
                  </IonLabel>
                </IonItemDivider>
                <IonItem>
                  <IonInput
                    autoCapitalize="sentences"
                    spellCheck="true"
                    type="text"
                    name="Location"
                    onIonChange={(e: any) => setLocation(e.detail.value)}
                    value={location}
                    clearInput
                    placeholder="Where is the job?"
                  ></IonInput>

                  {/* Map icon toggle */}
                  <IonButton
                    slot="end"
                    fill="outline"
                    color={hasMapValue ? "secondary" : "medium"}
                    onClick={() => setShowMapPin((v) => !v)}
                  >
                    <IonIcon
                      icon={mapOutline}
                    />
                  </IonButton>
                </IonItem>

                {showMapPin ? (
                  <div>
                    <IonItemDivider
                    >
                      <IonLabel
                        color={locationColor}
                      >
                        <strong>Map link / pin (optional):</strong>
                      </IonLabel>
                    </IonItemDivider>
                    <IonItem
                    >
                      <IonInput
                        type="text"
                        name="LocationCoordinates"
                        placeholder="Google/Apple Maps link, address, or lat,lng"
                        onIonChange={(e: any) => setLocationCoordinates(e.detail.value)}
                        value={locationCoordinates}
                        clearInput
                      ></IonInput>

                      <IonButtons
                        slot="end"
                      >
                        {/* <IonButton
                        fill="clear"
                        onClick={openMapPin}
                        disabled={!hasMapValue}
                      >
                        Open
                      </IonButton> */}
                      </IonButtons>
                    </IonItem>
                  </div>
                ) : null}
              </IonList>
            </div>
          </IonAccordion>

          {/* Pay & Staffing */}
          <IonAccordion
            value="payStaffing"
          >
            <IonItem
              slot="header"
              color="light"
            >
              <IonLabel>
                <strong>Pay & Staffing</strong>
              </IonLabel>
            </IonItem>

            <div slot="content">
              <IonList
                inset={true}
              >
                <IonItemDivider
                >
                  <IonLabel>
                    <strong>Pay:</strong>
                  </IonLabel>
                </IonItemDivider>
                <IonItem>
                  <IonInput
                    type="text"
                    name="Pay"
                    placeholder="Optional (e.g., $45/hr, $300 flat, varies)"
                    onIonChange={(e: any) => setPay(e.detail.value)}
                    value={pay}
                    clearInput
                  ></IonInput>
                </IonItem>

                <IonItemDivider
                >
                  <IonLabel
                    color={numWerkersColor}
                  >
                    <strong>Number of Werkers:</strong>
                  </IonLabel>
                </IonItemDivider>
                <IonItem
                  class="ion-align-items-center"
                >
                  <IonLabel
                    position="stacked"
                    color={numWerkersColor}
                  >
                    {`(`}+/- or Tap to Scroll{`)`}
                  </IonLabel>

                  <IonButtons
                    slot="end"
                  >
                    <IonButton
                      fill="outline"
                      onClick={() => setnumberOfWorkers((prev: any) => Math.max(1, Number(prev || 1) - 1))}
                    >
                      –
                    </IonButton>

                    <IonButton
                      fill="outline"
                      // color="medium"
                      onClick={() => setIsOpen(true)}
                      style={{ minWidth: "64px" }}
                    >
                      <strong>{numberOfWorkers}</strong>
                    </IonButton>

                    <IonButton
                      fill="outline"
                      onClick={() => setnumberOfWorkers((prev: any) => Math.min(50, Number(prev || 1) + 1))}
                    >
                      +
                    </IonButton>
                  </IonButtons>
                  <IonPicker
                    isOpen={isOpen}
                    onDidDismiss={() => setIsOpen(false)}
                    columns={[NumberColumn]}
                    buttons={[
                      { text: "Cancel", role: "cancel" },
                      {
                        text: "Confirm",
                        handler: (value) => {
                          setnumberOfWorkers(value.number.value);
                        },
                      },
                    ]}
                  ></IonPicker>
                </IonItem>
              </IonList>
            </div>
          </IonAccordion>

          {/* Notes */}
          <IonAccordion
            value="notes"
          >
            <IonItem
              slot="header"
              color="light"
            >
              <IonLabel>
                <strong>Notes:</strong>
              </IonLabel>
            </IonItem>

            <div slot="content">
              <IonList
                inset={true}
              >
                <IonItemDivider
                >
                  <IonLabel
                    color={numWerkersColor}
                  >
                    Job Notes:{""}
                  </IonLabel>
                </IonItemDivider>
                <IonItem>
                  <IonTextarea
                    autoCapitalize="sentences"
                    spellCheck="true"
                    name="Notes"
                    onIonChange={(e: any) => setNotes(e.detail.value)}
                    value={notes}
                    placeholder="everyone on this job can see this"
                  ></IonTextarea>
                </IonItem>
              </IonList>
            </div>
          </IonAccordion>
        </IonAccordionGroup>

        <div ref={bottomRef} />
          </div>
        </div>
      </div>
    );
  }

  // Mobile mode: render with IonPage wrapper
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          color="secondWarning"
        >
          <IonButton
            onClick={navigateBack}
            slot="start"
            fill="clear"
          >
            <IonIcon
              size="large"
              className="backArrowColor"
              icon={arrowBack}
            />
          </IonButton>

          <IonTitle>
            <strong>
              <h2>{headerText}</h2>
            </strong>
          </IonTitle>

          <IonButtons
            style={{ marginRight: "13px" }}
            slot="end"
          >
            <IonButton
              slot="start"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              <strong>{isSaving ? "Saving..." : "Save"}</strong>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent
        style={{ "--padding-bottom": "90px" }}
      >
        {/* CORE (always visible) */}
        <IonList
          inset={true}
        >
          <IonItemDivider>
            <IonLabel
              color={jobIdColor}
            >
              <strong>Job Name/ID:</strong>
            </IonLabel>
          </IonItemDivider>

          <IonItem>
            <IonInput
              placeholder="Job name, number or ID"
              type="text"
              name="JobId"
              spellCheck="true"
              autoCapitalize="sentences"
              onIonChange={(e: any) => setJobId(e.detail.value)}
              value={jobId}
              clearInput
            ></IonInput>
          </IonItem>

          <IonItemDivider>
            <IonLabel
              color={beginDateColor}
            >
              <strong>Start:</strong>
            </IonLabel>
          </IonItemDivider>
          <IonItem>
            <IonInput
              type="datetime-local"
              value={beginSelectedDate.substring(0, 19)}
              onIonChange={(e: any) => setBeginSelectedDate(e.detail.value)}
            ></IonInput>
          </IonItem>

          <IonItemDivider>
            <IonLabel
              color={endDateColor}
            >
              <strong>End:</strong>
            </IonLabel>
          </IonItemDivider>
          <IonItem>
            <IonInput
              type="datetime-local"
              value={endSelectedDate.substring(0, 19)}
              onIonChange={(e: any) => setEndSelectedDate(e.detail.value)}
            ></IonInput>
          </IonItem>
        </IonList>

        {/* SECTIONS (accordion, open by default) */}
        {/* SECTIONS (Accordion: open by default, uncontrolled) */}
        <IonAccordionGroup
          ref={setAccGroup as any}
          multiple={true}
        >
          {/* Repeat (disabled in Edit Job to avoid series create/continue conflicts) */}
          {!isJobEdit ? (
            <IonAccordion
              value="repeat"
            >
              <IonItem
                slot="header"
                color="light"
              >
                <IonLabel>
                  <strong>Repeat</strong>
                </IonLabel>
              </IonItem>

              <div slot="content">
                <IonList
                  inset={true}
                >
                  <IonItem>
                    <IonSelect
                      value={repeatPattern}
                      interface="popover"
                      onIonChange={(e: any) => {
                        const val = (e?.detail?.value as any) || "none";
                        setRepeatPattern(val);
                        setRepeatColor("dark");

                        if (val !== "none" && (!repeatOccurrences || repeatOccurrences < 2)) {
                          setRepeatOccurrences(2);
                        }
                      }}
                    >
                      <IonSelectOption
                        value="none"
                      >
                        None
                      </IonSelectOption>
                      <IonSelectOption
                        value="daily"
                      >
                        Daily
                      </IonSelectOption>
                      <IonSelectOption
                        value="weekly"
                      >
                        Weekly
                      </IonSelectOption>
                    </IonSelect>
                  </IonItem>

                  {repeatPattern !== "none" ? (
                    <IonItem
                    >
                      <IonLabel
                        position="stacked"
                        color={repeatOccColor}
                      >
                        <h2>Number of times recurring:</h2>
                      </IonLabel>

                      <IonInput
                        type="number"
                        inputmode="numeric"
                        value={repeatOccurrences}
                        min={2}
                        onIonChange={(e: any) => {
                          const raw = e?.target?.value ?? e?.detail?.value;
                          const n = Number(raw);
                          setRepeatOccurrences(Number.isFinite(n) ? n : 2);
                          setRepeatOccColor("dark");
                        }}
                      />
                    </IonItem>
                  ) : null}
                </IonList>
              </div>
            </IonAccordion>
          ) : null}

          {/* Details */}
          <IonAccordion
            value="details"
          >
            <IonItem
              slot="header"
              color="light"
            >
              <IonLabel>
                <strong>Details</strong>
              </IonLabel>
            </IonItem>

            <div slot="content">
              <IonList
                inset={true}
              >
                <IonItemDivider
                >
                  <IonLabel
                    color={companyColor}
                  >
                    <strong>Company:</strong>
                  </IonLabel>
                </IonItemDivider>
                <IonItem>
                  <IonInput
                    type="text"
                    name="Company"
                    spellCheck="true"
                    autoCapitalize="sentences"
                    onIonChange={(e: any) => setCompany(e.detail.value)}
                    value={company}
                    clearInput
                  ></IonInput>
                </IonItem>

                <IonItemDivider
                >
                  <IonLabel
                    color={locationColor}
                  >
                    <strong>Location:</strong>
                  </IonLabel>
                </IonItemDivider>
                <IonItem>
                  <IonInput
                    autoCapitalize="sentences"
                    spellCheck="true"
                    type="text"
                    name="Location"
                    onIonChange={(e: any) => setLocation(e.detail.value)}
                    value={location}
                    clearInput
                    placeholder="Where is the job?"
                  ></IonInput>

                  {/* Map icon toggle */}
                  <IonButton
                    slot="end"
                    fill="outline"
                    color={hasMapValue ? "secondary" : "medium"}
                    onClick={() => setShowMapPin((v) => !v)}
                  >
                    <IonIcon
                      icon={mapOutline}
                    />
                  </IonButton>
                </IonItem>

                {showMapPin ? (
                  <div>
                    <IonItemDivider
                    >
                      <IonLabel
                        color={locationColor}
                      >
                        <strong>Map link / pin (optional):</strong>
                      </IonLabel>
                    </IonItemDivider>
                    <IonItem
                    >
                      <IonInput
                        type="text"
                        name="LocationCoordinates"
                        placeholder="Google/Apple Maps link, address, or lat,lng"
                        onIonChange={(e: any) => setLocationCoordinates(e.detail.value)}
                        value={locationCoordinates}
                        clearInput
                      ></IonInput>

                      <IonButtons
                        slot="end"
                      >
                      </IonButtons>
                    </IonItem>
                  </div>
                ) : null}
              </IonList>
            </div>
          </IonAccordion>

          {/* Pay & Staffing */}
          <IonAccordion
            value="payStaffing"
          >
            <IonItem
              slot="header"
              color="light"
            >
              <IonLabel>
                <strong>Pay & Staffing</strong>
              </IonLabel>
            </IonItem>

            <div slot="content">
              <IonList
                inset={true}
              >
                <IonItemDivider
                >
                  <IonLabel>
                    <strong>Pay:</strong>
                  </IonLabel>
                </IonItemDivider>
                <IonItem>
                  <IonInput
                    type="text"
                    name="Pay"
                    placeholder="Optional (e.g., $45/hr, $300 flat, varies)"
                    onIonChange={(e: any) => setPay(e.detail.value)}
                    value={pay}
                    clearInput
                  ></IonInput>
                </IonItem>

                <IonItemDivider
                >
                  <IonLabel
                    color={numWerkersColor}
                  >
                    <strong>Number of Werkers:</strong>
                  </IonLabel>
                </IonItemDivider>
                <IonItem
                  class="ion-align-items-center"
                >
                  <IonLabel
                    position="stacked"
                    color={numWerkersColor}
                  >
                    {`(`}+/- or Tap to Scroll{`)`}
                  </IonLabel>

                  <IonButtons
                    slot="end"
                  >
                    <IonButton
                      fill="outline"
                      onClick={() => setnumberOfWorkers((prev: any) => Math.max(1, Number(prev || 1) - 1))}
                    >
                      –
                    </IonButton>

                    <IonButton
                      fill="outline"
                      onClick={() => setIsOpen(true)}
                      style={{ minWidth: "64px" }}
                    >
                      <strong>{numberOfWorkers}</strong>
                    </IonButton>

                    <IonButton
                      fill="outline"
                      onClick={() => setnumberOfWorkers((prev: any) => Math.min(50, Number(prev || 1) + 1))}
                    >
                      +
                    </IonButton>
                  </IonButtons>
                  <IonPicker
                    isOpen={isOpen}
                    onDidDismiss={() => setIsOpen(false)}
                    columns={[NumberColumn]}
                    buttons={[
                      { text: "Cancel", role: "cancel" },
                      {
                        text: "Confirm",
                        handler: (value) => {
                          setnumberOfWorkers(value.number.value);
                        },
                      },
                    ]}
                  ></IonPicker>
                </IonItem>
              </IonList>
            </div>
          </IonAccordion>

          {/* Notes */}
          <IonAccordion
            value="notes"
          >
            <IonItem
              slot="header"
              color="light"
            >
              <IonLabel>
                <strong>Notes:</strong>
              </IonLabel>
            </IonItem>

            <div slot="content">
              <IonList
                inset={true}
              >
                <IonItemDivider
                >
                  <IonLabel
                    color={numWerkersColor}
                  >
                    Job Notes:{""}
                  </IonLabel>
                </IonItemDivider>
                <IonItem>
                  <IonTextarea
                    autoCapitalize="sentences"
                    spellCheck="true"
                    name="Notes"
                    onIonChange={(e: any) => setNotes(e.detail.value)}
                    value={notes}
                    placeholder="everyone on this job can see this"
                  ></IonTextarea>
                </IonItem>
              </IonList>
            </div>
          </IonAccordion>
        </IonAccordionGroup>

        <div ref={bottomRef} />
      </IonContent>
    </IonPage>
  );
};

export default ScheduleJob;
